import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Coins, Loader2 } from "lucide-react";

export const PrixMPDialog = ({ sid, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [prix, setPrix] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows(null);
    api.get("/etat-stock/prix-mp", { params: { societe_id: sid } }).then((r) => {
      setRows(r.data);
      setPrix(Object.fromEntries(r.data.map((m) => [m.mp, m.prix.length === 1 ? String(m.prix[0]) : ""])));
    });
  }, [open, sid]);

  const save = async () => {
    const changes = Object.fromEntries(rows.filter((m) => prix[m.mp] !== "" && !(m.prix.length === 1 && Number(prix[m.mp]) === m.prix[0]))
      .map((m) => [m.mp, Number(String(prix[m.mp]).replace(",", "."))]));
    if (Object.values(changes).some((v) => Number.isNaN(v) || v < 0)) return toast.error("Prix invalide");
    setSaving(true);
    try {
      const r = await api.put("/etat-stock/prix-mp", { societe_id: sid, prix: changes });
      toast.success(`${r.data.lignes_modifiees} ligne(s) de recette mise(s) à jour`);
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button variant="outline" className="gap-2" onClick={() => setOpen(true)} data-testid="prix-mp-open-btn"><Coins className="h-4 w-4" /> Prix des matières</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="prix-mp-dialog">
          <DialogHeader>
            <DialogTitle>Prix de revient des matières premières</DialogTitle>
            <DialogDescription>Le prix saisi s'applique à toutes les recettes qui utilisent la matière ; coûts de revient et marges sont recalculés. Un prix saisi dans une fiche produit pour une année reste prioritaire pour ce produit et cette année.</DialogDescription>
          </DialogHeader>
          {!rows ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground border-b"><th className="py-2">Matière première</th><th>Unité</th><th>Utilisée dans</th><th className="w-36 text-right">Prix / unité (DA)</th></tr></thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.mp} className="border-b last:border-0" data-testid={`prix-mp-row-${m.mp}`}>
                    <td className="py-1.5 font-medium">{m.mp}</td>
                    <td>{m.unite}</td>
                    <td className="text-xs text-muted-foreground">{m.recettes.length} recette(s){m.prix.length > 1 && <span className="block text-amber-600">Prix différents : {m.prix.join(" / ")}</span>}</td>
                    <td className="text-right"><Input className="h-8 text-right" inputMode="decimal" value={prix[m.mp] ?? ""} placeholder={m.prix.join(" / ")}
                      onChange={(ev) => setPrix((p) => ({ ...p, [m.mp]: ev.target.value }))} data-testid={`prix-mp-input-${m.mp}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="prix-mp-cancel-btn">Annuler</Button>
            <Button onClick={save} disabled={!rows || saving} data-testid="prix-mp-save-btn">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
